// DRAG AND DROP from the « Icônes » / « Stickers » tiles (owner 2026-10-20:
// « I need to be able to drag and drop »).
//
// A tile still adds on a TAP (its own onClick). Pressing and moving it instead
// lifts a small copy that follows the pointer; released over a drop zone, the
// piece lands exactly under the pointer:
//
//   · mouse / pen — the drag starts after a 5 px move (a click stays a click);
//   · touch       — a long press (400 ms, finger still) lifts it, like the
//                   photo tray (useTouchDragPhoto). Moving earlier scrolls the
//                   panel as before.
//
// Drop zones opt in with `data-element-drop="<key>"`:
//   `cover:front` · `cover:back` · `cover:spine`  (the cover faces, admin)
//   `page:<n>`                                     (a content page)
// The zones are found by RECTANGLE, not elementFromPoint: the cover's element
// layers are pointer-events:none so the artwork under them stays clickable.
// The drop is announced on `window` as TILE_DROP_EVENT — the editor listens
// once, so no prop has to thread through the side panel and the phone sheet.
//
// While a drag is live, <body> carries `tile-dragging` (the phone's bottom
// sheet fades so the page under it can be reached — index.css).

import { useCallback, useRef } from 'react';
import type React from 'react';
import type { Cover, EditorIcon } from './api';
import type { Sticker } from './stickers/catalog';

export type TileDragPayload =
  | { kind: 'sticker'; sticker: Sticker }
  | { kind: 'coverIcon'; cover: Cover }
  | { kind: 'adminIcon'; icon: EditorIcon };

export interface TileDropDetail {
  payload: TileDragPayload;
  /** The zone's `data-element-drop` value. */
  target: string;
  /** Where the pointer was released, as fractions of the zone (0..1). */
  fx: number;
  fy: number;
}

export const TILE_DROP_EVENT = 'storiz:tile-drop';
export const DROP_ACTIVE_CLASS = 'element-drop-active';
const HOLD_MS = 400;
const TOUCH_CANCEL_PX = 8;
const MOUSE_START_PX = 5;

export interface RectLike { left: number; top: number; width: number; height: number }

/** The point as fractions of the rect (clamped to 0..1). */
export function dropFraction(rect: RectLike, x: number, y: number): { fx: number; fy: number } {
  const c = (v: number) => Math.max(0, Math.min(1, v));
  return {
    fx: rect.width > 0 ? c((x - rect.left) / rect.width) : 0.5,
    fy: rect.height > 0 ? c((y - rect.top) / rect.height) : 0.5,
  };
}

/** The drop zone under (x, y): the SMALLEST zone whose rect holds the point
 *  (the spine strip sits inside nothing, but a page could be nested in a
 *  bigger box one day — the innermost wins). */
export function pickDropZone<T>(zones: ReadonlyArray<{ item: T; rect: RectLike }>, x: number, y: number): T | null {
  let best: { item: T; area: number } | null = null;
  for (const z of zones) {
    const r = z.rect;
    if (r.width <= 0 || r.height <= 0) continue;
    if (x < r.left || x > r.left + r.width || y < r.top || y > r.top + r.height) continue;
    const area = r.width * r.height;
    if (!best || area < best.area) best = { item: z.item, area };
  }
  return best?.item ?? null;
}

export function findDropZone(x: number, y: number): { el: HTMLElement; key: string; fx: number; fy: number } | null {
  if (typeof document === 'undefined') return null;
  const zones = Array.from(document.querySelectorAll<HTMLElement>('[data-element-drop]'))
    .map((el) => ({ item: el, rect: el.getBoundingClientRect() as RectLike }));
  const el = pickDropZone(zones, x, y);
  if (!el) return null;
  const key = el.getAttribute('data-element-drop') ?? '';
  if (!key) return null;
  return { el, key, ...dropFraction(el.getBoundingClientRect(), x, y) };
}

/** The editor's side: `(detail) => void`, called on every drop. */
export function onTileDrop(listener: (d: TileDropDetail) => void): () => void {
  const h = (e: Event) => listener((e as CustomEvent<TileDropDetail>).detail);
  window.addEventListener(TILE_DROP_EVENT, h);
  return () => window.removeEventListener(TILE_DROP_EVENT, h);
}

interface Live {
  payload: TileDragPayload;
  ghost: HTMLDivElement;
  over: HTMLElement | null;
}

function makeGhost(src: string | null, x: number, y: number): HTMLDivElement {
  const g = document.createElement('div');
  g.setAttribute('aria-hidden', 'true');
  g.setAttribute('data-tile-ghost', '');
  Object.assign(g.style, {
    position: 'fixed', left: `${x}px`, top: `${y}px`, width: '64px', height: '64px',
    marginLeft: '-32px', marginTop: '-32px', borderRadius: '12px',
    background: 'rgba(255,255,255,0.92)',
    backgroundImage: src ? `url(${JSON.stringify(src)})` : '',
    backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25), 0 0 0 2px #C9A24A',
    zIndex: '100000', pointerEvents: 'none', transform: 'scale(1.06)',
  } as Partial<CSSStyleDeclaration>);
  document.body.appendChild(g);
  return g;
}

/**
 * `const bind = useTileDrag();` then spread `{...bind(payload, previewSrc)}`
 * on each tile (a <button> that keeps its own onClick for the tap).
 */
export function useTileDrag() {
  const live = useRef<Live | null>(null);
  /** A drag just ended: swallow the click the browser sends after it. */
  const swallowClick = useRef(false);

  const highlight = (x: number, y: number) => {
    const st = live.current;
    if (!st) return;
    const z = findDropZone(x, y);
    const el = z?.el ?? null;
    if (el !== st.over) {
      st.over?.classList.remove(DROP_ACTIVE_CLASS);
      el?.classList.add(DROP_ACTIVE_CLASS);
      st.over = el;
    }
  };

  const start = (payload: TileDragPayload, src: string | null, x: number, y: number) => {
    live.current = { payload, ghost: makeGhost(src, x, y), over: null };
    document.body.classList.add('tile-dragging');
    highlight(x, y);
  };

  const move = (x: number, y: number) => {
    const st = live.current;
    if (!st) return;
    st.ghost.style.left = `${x}px`;
    st.ghost.style.top = `${y}px`;
    highlight(x, y);
  };

  const finish = (x: number | null, y: number | null) => {
    const st = live.current;
    if (!st) return;
    live.current = null;
    st.over?.classList.remove(DROP_ACTIVE_CLASS);
    try { st.ghost.remove(); } catch { /* gone */ }
    document.body.classList.remove('tile-dragging');
    swallowClick.current = true;
    setTimeout(() => { swallowClick.current = false; }, 400);
    if (x == null || y == null) return;
    const z = findDropZone(x, y);
    if (!z) return;
    const detail: TileDropDetail = { payload: st.payload, target: z.key, fx: z.fx, fy: z.fy };
    window.dispatchEvent(new CustomEvent<TileDropDetail>(TILE_DROP_EVENT, { detail }));
  };

  return useCallback((payload: TileDragPayload, previewSrc: string | null) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      // Touch goes through the long-press path below (onTouchStart).
      if (e.pointerType === 'touch' || e.button !== 0) return;
      const sx = e.clientX, sy = e.clientY;
      let dragging = false;
      const onMove = (ev: PointerEvent) => {
        if (!dragging) {
          if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < MOUSE_START_PX) return;
          dragging = true;
          start(payload, previewSrc, ev.clientX, ev.clientY);
        }
        ev.preventDefault();
        move(ev.clientX, ev.clientY);
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        if (dragging) finish(ev.clientX, ev.clientY);
      };
      const onCancel = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        if (dragging) finish(null, null);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
    },
    onTouchStart: (e: React.TouchEvent<HTMLElement>) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1) return;
      const sx = t.clientX, sy = t.clientY;
      let lastX = sx, lastY = sy;
      const onPendingMove = (ev: TouchEvent) => {
        const p = ev.touches[0];
        if (!p) return;
        lastX = p.clientX; lastY = p.clientY;
        if (Math.hypot(p.clientX - sx, p.clientY - sy) > TOUCH_CANCEL_PX) cancelPending();
      };
      const cancelPending = () => {
        clearTimeout(timer);
        window.removeEventListener('touchmove', onPendingMove);
        window.removeEventListener('touchend', cancelPending);
        window.removeEventListener('touchcancel', cancelPending);
      };
      const onDragMove = (ev: TouchEvent) => {
        const p = ev.touches[0];
        if (!p) return;
        ev.preventDefault();                 // the page must not scroll under the drag
        lastX = p.clientX; lastY = p.clientY;
        move(p.clientX, p.clientY);
      };
      const onDragEnd = (ev: TouchEvent) => {
        window.removeEventListener('touchmove', onDragMove);
        window.removeEventListener('touchend', onDragEnd);
        window.removeEventListener('touchcancel', onDragCancel);
        const p = ev.changedTouches[0];
        finish(p ? p.clientX : lastX, p ? p.clientY : lastY);
      };
      const onDragCancel = () => {
        window.removeEventListener('touchmove', onDragMove);
        window.removeEventListener('touchend', onDragEnd);
        window.removeEventListener('touchcancel', onDragCancel);
        finish(null, null);
      };
      const timer = setTimeout(() => {
        cancelPending();
        try { navigator.vibrate?.(40); } catch { /* old Safari */ }
        start(payload, previewSrc, lastX, lastY);
        window.addEventListener('touchmove', onDragMove, { passive: false });
        window.addEventListener('touchend', onDragEnd);
        window.addEventListener('touchcancel', onDragCancel);
      }, HOLD_MS);
      window.addEventListener('touchmove', onPendingMove, { passive: true });
      window.addEventListener('touchend', cancelPending);
      window.addEventListener('touchcancel', cancelPending);
    },
    onClickCapture: (e: React.MouseEvent<HTMLElement>) => {
      // The click a drag ends with is not a tap.
      if (swallowClick.current) { e.preventDefault(); e.stopPropagation(); swallowClick.current = false; }
    },
    onContextMenu: (e: React.MouseEvent<HTMLElement>) => { e.preventDefault(); },
    onDragStart: (e: React.DragEvent<HTMLElement>) => { e.preventDefault(); },   // no native image drag
    style: { WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' } as React.CSSProperties,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
}
